import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { Button } from "@heroui/react";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { AdminAccessProvider } from "@src/auth/adminAccess";
import { DEV_BYPASS } from "@src/auth/devBypass";
import { KINDE_ORG_CODE } from "@src/auth/kindeConfig";

/**
 * Gate component that requires the user to be authenticated with the
 * `employee` or `bruce-uploader` role before rendering children.
 */
export const RequireAuth = ({ children }: { readonly children: ReactNode }): ReactNode => {
  if (DEV_BYPASS) return <>{children}</>;
  return <AuthGate>{children}</AuthGate>;
};

const AuthGate = ({ children }: { readonly children: ReactNode }): ReactNode => {
  const { isAuthenticated, isLoading, login, user, getRoles } = useKindeAuth();
  const [roles, setRoles] = useState<ReadonlyArray<{ key: string }> | null>(null);
  const [rolesLoading, setRolesLoading] = useState(true);

  // Fetch roles once authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setRoles(null);
      setRolesLoading(false);
      return;
    }
    let cancelled = false;
    const fetchRoles = async (): Promise<void> => {
      try {
        const result = await getRoles();
        if (!cancelled) setRoles(result);
      } catch {
        if (!cancelled) setRoles([]);
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    };
    void fetchRoles();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getRoles]);

  // Expose user to resolveCurrentUser
  useEffect(() => {
    if (user?.email) {
      (window as unknown as Record<string, unknown>).__KINDE_USER__ = {
        email: user.email,
        given_name: user.givenName,
      };
    }
    return () => {
      (window as unknown as Record<string, unknown>).__KINDE_USER__ = undefined;
    };
  }, [user?.email, user?.givenName]);

  if (isLoading || rolesLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <h1 className="text-xl font-semibold">NextDay Admin</h1>
        <p className="text-default-500">Sign in with your employee account to continue.</p>
        <Button variant="primary" onPress={() => void login({ orgCode: KINDE_ORG_CODE })}>
          Sign in
        </Button>
      </div>
    );
  }

  // Check for employee or bruce-uploader role
  const hasEmployeeRole = roles?.some((r) => r.key === "employee") ?? false;
  const hasBruceUploaderRole = roles?.some((r) => r.key === "bruce-uploader") ?? false;

  if (!hasEmployeeRole && !hasBruceUploaderRole) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <h1 className="text-xl font-semibold">Access Denied</h1>
        <p className="text-default-500">
          Your account ({user?.email}) does not have the required employee or bruce-uploader role.
        </p>
        <p className="text-xs text-default-400">
          Contact your administrator if you believe this is an error.
        </p>
      </div>
    );
  }

  return (
    <AdminAccessProvider role={hasEmployeeRole ? "admin" : "uploader"}>
      {children}
    </AdminAccessProvider>
  );
};
